import React, { useState, useEffect } from 'react'
import { Layout, Card, Button, Breadcrumb, Tree, Input, Form, Select, message, Spin, Space, Tag, Divider } from 'antd'
import { HomeOutlined, SaveOutlined, SendOutlined, PlusOutlined, EditOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { getTextbook, updateTextbook, type Textbook } from '../../api/manage/textbook'
import {
  listChapterByTextbook,
  addChapter,
  updateChapter,
  delChapter,
  submitChapter,
  type Chapter
} from '../../api/manage/chapter'
import { PermissionWrapper } from '../../components/PermissionWrapper'
import './TextbookEdit.css'

const { Sider, Content } = Layout
const { TextArea } = Input
const { Option } = Select

interface ChapterNode {
  key: string
  title: string
  chapter: Chapter
  children?: ChapterNode[]
}

const TextbookEdit: React.FC = () => {
  const navigate = useNavigate()
  const { textbookId } = useParams<{ textbookId: string }>()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [textbook, setTextbook] = useState<Textbook | null>(null)
  const [chapters, setChapters] = useState<Chapter[]>([])
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null)
  const [chapterContent, setChapterContent] = useState<string>('')
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])

  useEffect(() => {
    if (textbookId) {
      loadTextbook()
      loadChapters()
    }
  }, [textbookId])

  const loadTextbook = async () => {
    try {
      setLoading(true)
      const res = await getTextbook(Number(textbookId)) as { data?: Textbook }
      const book = res?.data ?? (res as unknown as Textbook)
      setTextbook(book)
      form.setFieldsValue({
        title: book.title,
        subject: book.subject,
        version: book.version,
        description: book.description,
        status: book.status,
      })
    } catch (error: any) {
      message.error('加载教材信息失败: ' + (error.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }

  const loadChapters = async () => {
    try {
      if (!textbookId) return
      const res = await listChapterByTextbook(Number(textbookId)) as { data?: Chapter[] }
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
      setChapters(list)
      if (list.length > 0 && !selectedChapter) {
        setSelectedChapter(list[0])
        loadChapterContent(list[0])
      }
    } catch (error: any) {
      message.error('加载章节列表失败: ' + (error.message || '未知错误'))
    }
  }

  const loadChapterContent = async (chapter: Chapter) => {
    if (chapter.contentId) {
      // TODO: 从MongoDB加载章节内容
      setChapterContent('')
    } else {
      setChapterContent('')
    }
  }

  const handleSaveTextbook = async () => {
    try {
      const values = await form.validateFields()
      if (!textbookId) return

      await updateTextbook({ ...values, textbookId: Number(textbookId) })
      message.success('保存成功')
      loadTextbook()
    } catch (error: any) {
      message.error('保存失败: ' + (error.message || '未知错误'))
    }
  }

  const handleAddChapter = async () => {
    try {
      if (!textbookId) return

      const newChapterNumber = chapters.length + 1
      await addChapter({
        textbookId: Number(textbookId),
        chapterNumber: newChapterNumber,
        title: `第${newChapterNumber}章`,
      })

      message.success('章节创建成功')
      loadChapters()
    } catch (error: any) {
      message.error('创建章节失败: ' + (error.message || '未知错误'))
    }
  }

  const handleSaveChapter = async () => {
    try {
      if (!selectedChapter) return

      await updateChapter({
        chapterId: selectedChapter.chapterId,
        title: selectedChapter.title,
        contentId: selectedChapter.contentId,
        wordCount: chapterContent.length,
      })

      message.success('章节保存成功')
      loadChapters()
    } catch (error: any) {
      message.error('保存章节失败: ' + (error.message || '未知错误'))
    }
  }

  const handleSubmitChapter = async () => {
    try {
      if (!selectedChapter) return

      await submitChapter(selectedChapter.chapterId)
      message.success('章节已提交审核')
      loadChapters()
    } catch (error: any) {
      message.error('提交失败: ' + (error.message || '未知错误'))
    }
  }

  const handleDeleteChapter = async (chapterId: number) => {
    try {
      await delChapter(chapterId)
      message.success('章节删除成功')
      loadChapters()
      setSelectedChapter(null)
    } catch (error: any) {
      message.error('删除失败: ' + (error.message || '未知错误'))
    }
  }

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      draft: { color: 'default', text: '草稿' },
      submitted: { color: 'processing', text: '已提交' },
      reviewing: { color: 'processing', text: '审核中' },
      approved: { color: 'success', text: '已通过' },
      rejected: { color: 'error', text: '已拒绝' },
      published: { color: 'success', text: '已发布' },
    }
    const statusInfo = statusMap[status] || { color: 'default', text: status }
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
  }

  const chapterTreeData: ChapterNode[] = chapters.map((chapter) => ({
    key: chapter.chapterId.toString(),
    title: `${chapter.chapterNumber}. ${chapter.title}`,
    chapter,
  }))

  if (loading && !textbook) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="textbook-edit-page">
      <div className="edit-header">
        <Breadcrumb
          items={[
            { title: <HomeOutlined />, href: '/' },
            { title: '教材管理', href: '/textbook' },
            { title: textbook?.title || '编辑教材' },
          ]}
        />
        <Space>
          <Button icon={<SaveOutlined />} onClick={handleSaveTextbook}>
            保存教材信息
          </Button>
          <PermissionWrapper permission="textbook:chapter:submit">
            <Button type="primary" icon={<SendOutlined />} onClick={handleSubmitChapter} disabled={!selectedChapter}>
              提交审核
            </Button>
          </PermissionWrapper>
        </Space>
      </div>

      <Layout className="edit-layout">
        <Sider width={280} className="chapter-sidebar">
          <Card 
            title="章节列表" 
            size="small"
            extra={
              <PermissionWrapper permission="textbook:chapter:add">
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddChapter}>
                  添加
                </Button>
              </PermissionWrapper>
            }
          >
            <Tree
              treeData={chapterTreeData}
              selectedKeys={selectedChapter ? [selectedChapter.chapterId.toString()] : []}
              expandedKeys={expandedKeys}
              onSelect={(keys) => {
                if (keys.length > 0) {
                  const chapter = chapters.find(c => c.chapterId.toString() === keys[0])
                  if (chapter) {
                    setSelectedChapter(chapter)
                    loadChapterContent(chapter)
                  }
                }
              }}
              onExpand={setExpandedKeys}
            />
          </Card>
        </Sider>

        <Content className="edit-content">
          <Card className="editor-card">
            <Form form={form} layout="vertical">
              <Form.Item label="教材标题" name="title" rules={[{ required: true, message: '请输入教材标题' }]}>
                <Input placeholder="请输入教材标题" />
              </Form.Item>
              <Form.Item label="学科" name="subject">
                <Select placeholder="请选择学科">
                  <Option value="中医基础">中医基础</Option>
                  <Option value="中药">中药</Option>
                  <Option value="中医临床">中医临床</Option>
                  <Option value="西医临床">西医临床</Option>
                </Select>
              </Form.Item>
              <Form.Item label="版本" name="version">
                <Input placeholder="例如：v1.0" />
              </Form.Item>
              <Form.Item label="描述" name="description">
                <TextArea rows={4} placeholder="请输入教材描述" />
              </Form.Item>
              <Form.Item label="状态" name="status">
                <Select>
                  <Option value="draft">草稿</Option>
                  <Option value="reviewing">审核中</Option>
                  <Option value="published">已发布</Option>
                </Select>
              </Form.Item>
            </Form>

            <Divider>章节编辑</Divider>

            {selectedChapter ? (
              <div>
                <Space style={{ marginBottom: 16 }}>
                  <h3>{selectedChapter.title}</h3>
                  {getStatusTag(selectedChapter.status)}
                  {selectedChapter.wordCount && (
                    <Tag>字数: {selectedChapter.wordCount}</Tag>
                  )}
                </Space>

                <TextArea
                  rows={20}
                  value={chapterContent}
                  onChange={(e) => setChapterContent(e.target.value)}
                  placeholder="请输入章节内容..."
                  style={{ fontFamily: 'monospace' }}
                />

                <Space style={{ marginTop: 16 }}>
                  <Button icon={<SaveOutlined />} onClick={handleSaveChapter}>
                    保存章节
                  </Button>
                  <PermissionWrapper permission="textbook:chapter:submit">
                    <Button type="primary" icon={<SendOutlined />} onClick={handleSubmitChapter}>
                      提交审核
                    </Button>
                  </PermissionWrapper>
                  <Button 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={() => handleDeleteChapter(selectedChapter.chapterId)}
                  >
                    删除章节
                  </Button>
                </Space>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <FileTextOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                <p>请选择或创建章节开始编辑</p>
              </div>
            )}
          </Card>
        </Content>

        <Sider width={300} className="info-sidebar">
          <Card title="章节信息" size="small" className="info-card">
            {selectedChapter ? (
              <div>
                <p><strong>章节编号:</strong> {selectedChapter.chapterNumber}</p>
                <p><strong>标题:</strong> {selectedChapter.title}</p>
                <p><strong>状态:</strong> {getStatusTag(selectedChapter.status)}</p>
                {selectedChapter.wordCount && (
                  <p><strong>字数:</strong> {selectedChapter.wordCount}</p>
                )}
                {selectedChapter.createTime && (
                  <p><strong>创建时间:</strong> {new Date(selectedChapter.createTime).toLocaleString()}</p>
                )}
              </div>
            ) : (
              <p style={{ color: '#999' }}>未选择章节</p>
            )}
          </Card>

          <Card title="编写进度" size="small" className="info-card" style={{ marginTop: 16 }}>
            <div>
              <p><strong>总章节数:</strong> {chapters.length}</p>
              <p><strong>已完成:</strong> {chapters.filter(c => c.status === 'published').length}</p>
              <p><strong>审核中:</strong> {chapters.filter(c => c.status === 'reviewing' || c.status === 'submitted').length}</p>
              <p><strong>草稿:</strong> {chapters.filter(c => c.status === 'draft').length}</p>
            </div>
          </Card>
        </Sider>
      </Layout>
    </div>
  )
}

export default TextbookEdit
